/**
 * Edge-Op Controller
 *
 * State machine that orchestrates the radial menu + pick-target flow for
 * the LineTool's edge ops (parallel / perpendicular / distance).
 *
 * Flow (from a LineMode's perspective):
 *
 *   1. LineMode detects a click (not a drag) on a prompt handle and calls
 *      `openMenuFor(prompt)`.
 *   2. Controller opens the radial menu, takes over input until done.
 *   3. While `isActive()`, the LineMode forwards `mousedown` / `keydown` to
 *      the controller and skips its own handling.
 *   4. On completion (or cancel), the controller restores state, hides the
 *      menu, and tells the mode to refresh the preview via the host.
 *
 * The controller is mode-agnostic: it depends only on a small `LineModeHost`
 * surface (prompt list, hit tester, visualizer, preview hook), so both
 * `ManualLineMode` and `AutomaticLineMode` reuse the same instance type.
 */

import * as THREE from 'three';
import type { Viewer } from '../../../../viewer/viewer';
import type { LinePrompt } from './types';
import type { LineSelectionHitTester } from './LineSelectionHitTester';
import type { LinePromptVisualizer } from './LinePromptVisualizer';
import { RadialMenu, RadialMenuItem } from '../radialMenu/RadialMenu';
import { setEdgeParallelTo, setEdgePerpendicularTo, setEdgeDistanceTo, averageDirection, perpendicularDistance } from './EdgeOps';
import { PointoramaRoom } from '../../../../utils/PointoramaRoom';
import { getSnapStepDisplay, metersToDisplay, displayToMeters } from './distanceUnits';
import { EdgeHighlight, EDGE_HIGHLIGHT_COLOR } from './EdgeHighlight';

/** Minimal surface the controller needs from a LineMode. Method-based
 *  rather than getter-based so callers can wire it with simple arrow
 *  functions that close over `this` lexically without aliasing it. */
export interface LineModeHost {
  getLinePrompts(): LinePrompt[];
  getHitTester(): LineSelectionHitTester;
  getVisualizer(): LinePromptVisualizer;
  /** Scene the controller can render overlay geometry into (drawn on top, depth-test off). */
  getOverlayScene(): THREE.Scene;
  updateShapePreview(): void;
  /** Push a translated hint key onto the existing quest/hint stack. */
  pushHintOverride(key: string): void;
  /** Pop the hint pushed via `pushHintOverride`. */
  popHintOverride(): void;
  /**
   * True iff the current prompt set still resolves to a valid polygon
   * (≥3 vertices and every regular prompt contributes one). The controller
   * runs this on commit; if false, it reverts the source's pre-op state.
   * Same predicate the drag flow uses to roll back self-intersecting moves.
   */
  isShapeValid(): boolean;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'menu' }
  | { kind: 'pick'; op: 'parallel' | 'perpendicular' | 'distance' }
  | { kind: 'input'; op: 'distance'; target: TargetRef };

/**
 * A snap/op target is either a LinePrompt (an edge handle in the active
 * edit shape) or a room wall (one segment of a room polygon on the floor).
 * Both expose a `worldPoint` + `direction` so the ops module treats them
 * uniformly.
 */
interface TargetRef {
  /** Stable id for highlight + dedupe. Prompt id, or `room:<id>:<edgeIndex>` for walls. */
  id: string;
  worldPoint: THREE.Vector3;
  direction: THREE.Vector3;
  /** Original prompt, when the target IS a prompt. Null for room walls. */
  promptRef: LinePrompt | null;
}

/** Inline icons — three-character glyphs work fine and match ContextMenu's vibe. */
const SVG_BASE = 'fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" xmlns="http://www.w3.org/2000/svg"';

// Two parallel lines, rotated 45° about the icon center so they read as a
// diagonal "parallel" glyph.
const ICON_PARALLEL_SVG = `<svg ${SVG_BASE} width="22" height="22">
  <path stroke-linecap="round" transform="rotate(45 12 12)" d="M7 4 L7 20 M17 4 L17 20" />
</svg>`;

// Disconnected T: a flat horizontal line with a separate vertical stem
// centered above it (a gap between them), reading as "perpendicular".
const ICON_PERPENDICULAR_SVG = `<svg ${SVG_BASE} width="22" height="22">
  <path stroke-linecap="round" d="M5 17 L19 17 M12 13 L12 4" />
</svg>`;

const ICON_DISTANCE_SVG = `<svg ${SVG_BASE} width="22" height="22">
  <path stroke-linecap="round" d="M5 5 L5 19 M19 5 L19 19 M5 12 L19 12 M5 12 L8 9 M5 12 L8 15 M19 12 L16 9 M19 12 L16 15" />
</svg>`;

/** Red flash on the source edge when an alignment op is reverted. */
const REVERT_FLASH_COLOR = 0xff5252;
const REVERT_FLASH_MS = 1200;

export class EdgeOpController {
  private menu: RadialMenu;
  private phase: Phase = { kind: 'idle' };
  private source: LinePrompt | null = null;
  /** Snapshots so cancel can fully restore the source prompt. */
  private snapshotDirection: THREE.Vector3 | null = null;
  private snapshotWorldPoint: THREE.Vector3 | null = null;
  /** Targets gathered during a multi-pick (parallel/perpendicular). Map keyed by id for stable lookup. */
  private targets: Map<string, TargetRef> = new Map();
  /** Cursor's current hover target during a pick phase; drives the highlight on hover. */
  private hoverTargetId: string | null = null;
  /** True while we've pushed an edge-op hint that needs popping on exit. */
  private hintPushed = false;
  /** THREE group holding the source→target relationship (dashed) lines. Re-rendered every `refreshHighlight()`. */
  private overlay: THREE.Group | null = null;
  /** Shared cyan highlight for the target wall segments. Lazily bound to the overlay scene. */
  private edgeHighlight: EdgeHighlight | null = null;
  /** Transient red flash of the source edge, shown when a commit reverts.
   *  Deliberately outlives the teardown — it IS the failure feedback. */
  private revertFlash: EdgeHighlight | null = null;
  private revertFlashTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly viewer: Viewer,
    private readonly host: LineModeHost,
  ) {
    this.menu = new RadialMenu(viewer);
  }

  isActive(): boolean {
    return this.phase.kind !== 'idle';
  }

  /** Open the radial menu on a clicked source handle. */
  openMenuFor(source: LinePrompt): void {
    this.cancel(); // Tear down any stale state first.
    this.source = source;
    this.snapshotDirection = source.direction.clone();
    this.snapshotWorldPoint = source.worldPoint.clone();
    this.phase = { kind: 'menu' };
    this.refreshHighlight();

    const items: RadialMenuItem[] = [
      {
        id: 'parallel',
        iconSvg: ICON_PARALLEL_SVG,
        tooltip: this.viewer.translationService.t('Align parallel to another edge'),
        onClick: () => this.startPick('parallel'),
      },
      {
        id: 'perpendicular',
        iconSvg: ICON_PERPENDICULAR_SVG,
        tooltip: this.viewer.translationService.t('Align perpendicular to another edge'),
        onClick: () => this.startPick('perpendicular'),
      },
      {
        id: 'distance',
        iconSvg: ICON_DISTANCE_SVG,
        tooltip: this.viewer.translationService.t('Set perpendicular distance to another edge'),
        onClick: () => this.startPick('distance'),
      },
    ];
    // Dismissing the menu (outside-click / Esc) must also drop the prompt's
    // selection highlight — cancel() restores the snapshot and clears the
    // highlight via teardown → refreshHighlight.
    this.menu.show({ type: 'items', items, onClose: () => this.cancel() }, source.worldPoint);
  }

  /**
   * Forwarded mouse-down from the LineMode. Returns `true` when consumed
   * (the mode should skip its own handling).
   */
  onMouseDown(event: MouseEvent, mouse: THREE.Vector2): boolean {
    if (this.phase.kind !== 'pick') return false;
    if (event.button !== 0) return false; // only left-click picks targets

    const target = this.pickTarget(mouse);
    if (!target) {
      // Clicking empty space cancels in `pick` phase.
      this.cancel();
      return true;
    }

    // Can't pick the source itself as target.
    if (this.source && target.id === this.source.id) return true;

    if (this.phase.op === 'distance') {
      // Distance is single-target: capture and morph the menu to an input.
      this.phase = { kind: 'input', op: 'distance', target };
      this.targets.clear();
      this.targets.set(target.id, target);
      this.refreshHighlight();
      this.openDistanceInput(target);
      return true;
    }

    // Parallel / perpendicular — shift-click adds to the averaging set
    // and stays in pick mode for more refs; normal click replaces and
    // immediately confirms.
    if (event.shiftKey) {
      if (this.targets.has(target.id)) {
        this.targets.delete(target.id);
      } else {
        this.targets.set(target.id, target);
      }
      this.refreshHighlight();
      this.applyAverage(this.phase.op);
      return true;
    }

    this.targets.clear();
    this.targets.set(target.id, target);
    this.refreshHighlight();
    this.applyAverage(this.phase.op);
    this.commitOrRevert();
    return true;
  }

  /**
   * Forwarded mouse-move from the LineMode during a pick phase. Used to
   * highlight whichever edge handle / room wall is currently hovered so
   * the user has a clear "this would be the target" affordance.
   */
  onMouseMove(_event: MouseEvent, mouse: THREE.Vector2): void {
    if (this.phase.kind !== 'pick') return;
    const target = this.pickTarget(mouse);
    const newId = target?.id ?? null;
    if (newId === this.hoverTargetId) return;
    this.hoverTargetId = newId;
    this.viewer.renderer.domElement.style.cursor = newId ? 'pointer' : 'crosshair';
    this.refreshHighlight();
  }

  /** Forwarded keydown. Returns `true` when consumed. */
  onKeyDown(event: KeyboardEvent): boolean {
    if (this.phase.kind === 'idle') return false;
    if (event.key === 'Escape') {
      this.cancel();
      return true;
    }
    return false;
  }

  /**
   * Forwarded keyup. When the user releases Shift during a parallel /
   * perpendicular pick AND at least one target is locked in, commit the
   * average and exit pick mode — matches the "shift to collect, release
   * to confirm" workflow the radial menu advertises.
   */
  onKeyUp(event: KeyboardEvent): void {
    if (this.phase.kind !== 'pick') return;
    if (this.phase.op === 'distance') return;
    if (event.key !== 'Shift') return;
    if (this.targets.size === 0) return;
    // Average already applied on every shift-click — validate and commit.
    this.commitOrRevert();
  }

  /**
   * Hit-test for pick targets: tries the existing prompt-sphere hit first
   * (matches the drag-init behavior), then falls back to room-wall edge
   * picking. Room edges are matched by raycasting against each room's
   * `backgroundLine` (a thick invisible line the room already maintains
   * for click-target use elsewhere) and resolving which polygon segment
   * the hit landed on.
   */
  private pickTarget(mouse: THREE.Vector2): TargetRef | null {
    // 1. Prompt handle?
    const hoveredPrompt = this.host.getHitTester().findHoveredPromptSphere(mouse, this.host.getLinePrompts());
    if (hoveredPrompt) {
      return {
        id: hoveredPrompt.id,
        worldPoint: hoveredPrompt.worldPoint,
        direction: hoveredPrompt.direction,
        promptRef: hoveredPrompt,
      };
    }

    // 2. Room wall? Raycast against every room background line on the floor.
    const wallTargets: THREE.Object3D[] = [];
    for (const room of this.viewer.scene.rooms as PointoramaRoom[]) {
      if (!room.visible || !room.finished) continue;
      if (room.backgroundLine) wallTargets.push(room.backgroundLine);
    }
    if (wallTargets.length === 0) return null;
    const hits = this.viewer.inputHandler.raycaster.intersectObjects(wallTargets);
    const hit = hits[0];
    if (!hit) return null;

    // Resolve which polygon edge the hit landed on so we can grab its
    // direction. The closest segment to the hit point wins.
    let parent: THREE.Object3D | null = hit.object.parent;
    while (parent && !(parent instanceof PointoramaRoom)) parent = parent.parent;
    if (!parent) return null;
    const room = parent as PointoramaRoom;
    const polygon = room.getPolygon();
    if (polygon.length < 2) return null;

    let bestIdx = 0;
    let bestDist = Infinity;
    const hitWorld = hit.point;
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i];
      const b = polygon[(i + 1) % polygon.length];
      const d = pointToSegmentDistance(hitWorld, a, b);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const a = polygon[bestIdx];
    const b = polygon[(bestIdx + 1) % polygon.length];
    const midpoint = new THREE.Vector3((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
    const direction = new THREE.Vector3(b.x - a.x, b.y - a.y, 0).normalize();
    return {
      id: `room:${room.identifier}:${bestIdx}`,
      worldPoint: midpoint,
      direction,
      promptRef: null,
    };
  }

  /** Restore state, hide the menu. Safe to call repeatedly. */
  cancel(): void {
    if (this.source && this.snapshotDirection && this.snapshotWorldPoint) {
      this.source.direction.copy(this.snapshotDirection);
      this.source.worldPoint.copy(this.snapshotWorldPoint);
      this.host.updateShapePreview();
    }
    this.teardown();
  }

  /** Commit current state and clean up without restoring snapshots. */
  private finish(): void {
    this.teardown();
  }

  /**
   * Final-commit gate for parallel/perpendicular ops. If applying the
   * average produced an invalid polygon (self-intersection, collapsed
   * vertex, prompt count mismatch), restore the source's pre-op snapshot
   * and tear down — same predicate the drag flow uses to roll back bad
   * moves. Intermediate shift-clicks bypass this check so the user sees
   * the running average even when momentarily invalid.
   */
  private commitOrRevert(): void {
    if (this.host.isShapeValid()) {
      this.finish();
      return;
    }
    // Invalid → revert the source to its pre-op snapshot AND say so: a
    // silent rollback is indistinguishable from success when the edges were
    // already nearly aligned. `cancel()` handles the restore + teardown.
    this.notifyReverted();
    this.cancel();
  }

  /**
   * Failure feedback for a reverted edge op: a warning toast (via the scene
   * event bridge in usePotreeEvents) plus a short red flash along the source
   * edge, locating WHERE the alignment could not be applied.
   */
  private notifyReverted(): void {
    this.viewer.scene.dispatchEvent({ type: 'edge_op_reverted_notification' });

    const source = this.source;
    if (!source) return;
    const dir = source.direction.clone().normalize();
    const start = source.worldPoint.clone().sub(dir.clone().multiplyScalar(source.negativeLength ?? 1));
    const end = source.worldPoint.clone().add(dir.clone().multiplyScalar(source.positiveLength ?? 1));

    if (this.revertFlashTimer) clearTimeout(this.revertFlashTimer);
    this.revertFlash ??= new EdgeHighlight(this.host.getOverlayScene(), { color: REVERT_FLASH_COLOR, linewidth: 4, renderOrder: 1300 });
    this.revertFlash.setEdges([{ start, end }]);
    this.revertFlashTimer = setTimeout(() => {
      this.revertFlash?.clear();
      this.revertFlashTimer = null;
    }, REVERT_FLASH_MS);
  }

  private teardown(): void {
    if (this.hintPushed) {
      this.host.popHintOverride();
      this.hintPushed = false;
    }
    this.source = null;
    this.snapshotDirection = null;
    this.snapshotWorldPoint = null;
    this.targets.clear();
    this.hoverTargetId = null;
    this.phase = { kind: 'idle' };
    this.menu.hide();
    this.viewer.renderer.domElement.style.cursor = 'default';
    this.clearOverlay();
    this.refreshHighlight();
  }

  /** Tear down the relationship-line + wall-highlight overlay. */
  private clearOverlay(): void {
    this.edgeHighlight?.clear();
    if (!this.overlay) return;
    const overlayScene = this.host.getOverlayScene();
    overlayScene.remove(this.overlay);
    this.overlay.traverse((obj) => {
      const mesh = obj as THREE.Mesh | THREE.Line;
      if ((mesh as THREE.Mesh).geometry) (mesh as THREE.Mesh).geometry.dispose();
      const mat = (mesh as THREE.Mesh).material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else if (mat) (mat as THREE.Material).dispose();
    });
    this.overlay = null;
  }

  /**
   * Rebuild the overlay group: a dashed line from the source midpoint to
   * each target midpoint, plus a solid highlight segment overlaid on the
   * picked wall span (for room-wall targets) or a small sphere at the
   * target prompt position. The set being highlighted is the union of
   * `targets` and the current hover target.
   */
  private renderOverlay(): void {
    this.clearOverlay();
    if (this.phase.kind !== 'pick' && this.phase.kind !== 'input') return;
    if (!this.source) return;

    const group = new THREE.Group();
    group.renderOrder = 1200;
    const sourceMid = this.source.worldPoint.clone();

    const all: TargetRef[] = [];
    for (const t of this.targets.values()) all.push(t);
    if (this.hoverTargetId) {
      const hover = this.resolveTargetById(this.hoverTargetId);
      if (hover && !this.targets.has(hover.id)) all.push(hover);
    }

    // For the distance op, route the dashed line from source midpoint
    // perpendicular to the target's infinite line (so the visual reads
    // as "this is the perpendicular distance"). For parallel/perpendicular,
    // a straight midpoint-to-midpoint line suffices since the geometry
    // there is about direction, not distance.
    const isDistance = (this.phase.kind === 'pick' && this.phase.op === 'distance') || this.phase.kind === 'input';

    const segments: { start: THREE.Vector3; end: THREE.Vector3 }[] = [];
    for (const t of all) {
      const endPoint = isDistance ? perpendicularFootOnLine(sourceMid, t.worldPoint, t.direction) : t.worldPoint.clone();
      const lineGeom = new THREE.BufferGeometry().setFromPoints([sourceMid, endPoint]);
      const lineMat = new THREE.LineDashedMaterial({ color: EDGE_HIGHLIGHT_COLOR, dashSize: 0.15, gapSize: 0.08, transparent: true, opacity: 0.85, depthTest: false });
      const line = new THREE.Line(lineGeom, lineMat);
      line.computeLineDistances();
      line.renderOrder = 1200;
      group.add(line);

      // Target edge segment (so the wall being aligned to lights up). Drawn via
      // the shared EdgeHighlight so it matches the opening-gizmo highlight.
      segments.push({
        start: t.worldPoint.clone().sub(t.direction.clone().multiplyScalar(0.6)),
        end: t.worldPoint.clone().add(t.direction.clone().multiplyScalar(0.6)),
      });
    }

    this.host.getOverlayScene().add(group);
    this.overlay = group;

    if (!this.edgeHighlight) this.edgeHighlight = new EdgeHighlight(this.host.getOverlayScene());
    this.edgeHighlight.setEdges(segments);
  }

  private resolveTargetById(id: string): TargetRef | null {
    const prompt = this.host.getLinePrompts().find((p) => p.id === id);
    if (prompt) {
      return { id: prompt.id, worldPoint: prompt.worldPoint, direction: prompt.direction, promptRef: prompt };
    }
    // Room wall id format: `room:<roomId>:<edgeIndex>`.
    const parts = id.split(':');
    if (parts.length === 3 && parts[0] === 'room') {
      const room = (this.viewer.scene.rooms as PointoramaRoom[]).find((r) => r.identifier === parts[1]);
      if (room) {
        const polygon = room.getPolygon();
        const idx = parseInt(parts[2], 10);
        if (idx >= 0 && idx < polygon.length) {
          const a = polygon[idx];
          const b = polygon[(idx + 1) % polygon.length];
          return {
            id,
            worldPoint: new THREE.Vector3((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2),
            direction: new THREE.Vector3(b.x - a.x, b.y - a.y, 0).normalize(),
            promptRef: null,
          };
        }
      }
    }
    return null;
  }

  dispose(): void {
    this.cancel();
    this.menu.dispose();
  }

  // ─── Internal ───────────────────────────────────────────────────────

  private startPick(op: 'parallel' | 'perpendicular' | 'distance'): void {
    if (!this.source) return;
    this.phase = { kind: 'pick', op };
    this.targets.clear();
    this.refreshHighlight();
    this.viewer.renderer.domElement.style.cursor = 'crosshair';
    // Hide the radial buttons (we're past the menu stage). The user gets
    // a hint via the existing quest/hint system so the messaging is in
    // the same place all other LineTool hints live.
    this.menu.hide();
    this.hintPushed = true;
    const key = op === 'distance' ? 'edge_op_pick_distance' : op === 'parallel' ? 'edge_op_pick_parallel' : 'edge_op_pick_perpendicular';
    this.host.pushHintOverride(key);
  }

  private applyAverage(op: 'parallel' | 'perpendicular'): void {
    if (!this.source) return;
    const refs = Array.from(this.targets.values());
    if (refs.length === 0) return;

    const avg = averageDirection(refs);
    if (!avg) return;
    if (op === 'parallel') setEdgeParallelTo(this.source, avg);
    else setEdgePerpendicularTo(this.source, avg);
    this.host.updateShapePreview();
  }

  private openDistanceInput(target: TargetRef): void {
    if (!this.source) return;
    // Perpendicular distance between the two edge midpoints — equivalent
    // to "if you slide the source edge along its own axis until directly
    // opposite the target's midpoint, how far apart are they."
    const currentDist = perpendicularDistance(this.source.worldPoint, target.worldPoint, target.direction);
    // Drop any leftover pick-phase hint before showing the input.
    if (this.hintPushed) {
      this.host.popHintOverride();
      this.hintPushed = false;
    }
    // Edit in the user's active display unit; geometry stays metric, so we
    // convert the seed value out and the typed value back in. The input step
    // matches the snap grid (5 cm in m, 1 inch in ft) for consistent arrows.
    const unit = this.viewer.lengthUnitDisplay;
    const stepDisplay = getSnapStepDisplay(unit.code);
    // The menu was hidden when we entered pick mode; bring it back so the
    // input field actually renders. Anchor on the source handle so the
    // input sits next to the edge being modified.
    this.menu.show(
      {
        type: 'input',
        label: this.viewer.translationService.t('dist'),
        unit: unit.code,
        step: String(stepDisplay),
        initialValue: metersToDisplay(currentDist, unit).toFixed(2),
        onConfirm: (raw) => {
          const parsed = parseFloat(raw);
          if (!isFinite(parsed) || parsed < 0) {
            this.cancel();
            return;
          }
          if (!this.source) {
            this.cancel();
            return;
          }
          setEdgeDistanceTo(this.source, target.worldPoint, target.direction, displayToMeters(parsed, unit));
          this.host.updateShapePreview();
          this.viewer.renderer.domElement.style.cursor = 'default';
          this.commitOrRevert();
        },
        onCancel: () => this.cancel(),
      },
      this.source.worldPoint,
    );
  }

  /**
   * Push the prompt-handle highlight set (source + target prompts + current
   * hover when it's a prompt) into the visualizer, then re-render the
   * overlay group with relationship lines + wall-segment highlights.
   */
  private refreshHighlight(): void {
    const promptIds: Set<string> = new Set();
    for (const t of this.targets.values()) {
      if (t.promptRef) promptIds.add(t.id);
    }
    if (this.source) promptIds.add(this.source.id);
    if (this.hoverTargetId && this.host.getLinePrompts().some((p) => p.id === this.hoverTargetId)) {
      promptIds.add(this.hoverTargetId);
    }
    if (promptIds.size === 0) {
      this.host.getVisualizer().updatePromptVisuals(this.host.getLinePrompts(), null, null, null);
    } else {
      this.host.getVisualizer().updatePromptVisuals(this.host.getLinePrompts(), null, promptIds, null);
    }
    this.renderOverlay();
  }
}

/** Foot of perpendicular from `p` onto the infinite XY line through `lineOrigin` with `lineDir`. */
function perpendicularFootOnLine(p: THREE.Vector3, lineOrigin: THREE.Vector3, lineDir: THREE.Vector3): THREE.Vector3 {
  const dx = lineDir.x;
  const dy = lineDir.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return p.clone();
  const t = ((p.x - lineOrigin.x) * dx + (p.y - lineOrigin.y) * dy) / len2;
  return new THREE.Vector3(lineOrigin.x + t * dx, lineOrigin.y + t * dy, p.z);
}

/** XY distance from a point to a segment. */
function pointToSegmentDistance(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
  const ax = a.x, ay = a.y, bx = b.x, by = b.y, px = p.x, py = p.y;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}
